import React, { Component } from 'react';
import { Button, Flex, Text } from 'rebass';
import styled from 'styled-components';

const Wrapper = styled.div`
  padding-left: 60px;
  padding-top: 40px;
  @media screen and (max-width: 52em) {
    padding-left: 12px;
  }
`;

const Container = styled(Flex)`
  flex: 1;
  flex-direction: column;
  width: 100%;
  max-width: 700px;
`;

const Title = styled(Text)`
  font-weight: 600;
  font-size: 22px;
  margin-bottom: 8px;
`;

const Description = styled(Text)`
  font-weight: 300;
  font-size: 14px;
  color: #555;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const Section = styled(Flex)`
  flex-direction: column;
  padding: 16px 20px;
`;

const PrefRow = styled(Flex)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #eee;
`;

const PrefInfo = styled(Flex)`
  flex-direction: column;
  flex: 1;
  margin-right: 16px;
`;

const PrefLabel = styled(Text)`
  font-weight: 500;
  font-size: 15px;
  color: #344f79;
`;

const PrefDesc = styled(Text)`
  font-weight: 300;
  font-size: 13px;
  color: #888;
  margin-top: 2px;
`;

const ToggleLabel = styled.label`
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) => (props.checked ? '#344f79' : '#ccc')};
  border-radius: 24px;
  transition: 0.3s;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:before {
    content: '';
    position: absolute;
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: 0.3s;
    transform: ${(props) => (props.checked ? 'translateX(20px)' : 'translateX(0)')};
  }
`;

const SavedMessage = styled(Text)`
  font-size: 14px;
  color: #344f79;
  font-weight: 500;
  margin-left: 16px;
`;

function getCookie(name) {
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2)
    return parts
      .pop()
      .split(';')
      .shift();
  return null;
}

function readConsentState() {
  const analyticsRaw = getCookie('fs-consent-analytics_storage');
  const marketingRaw = getCookie('fs-consent-ad_storage');
  const personalizationRaw = getCookie('fs-consent-personalization_storage');

  // If individual cookies exist, use them
  if (analyticsRaw !== null || marketingRaw !== null) {
    return {
      analytics: analyticsRaw === 'true',
      marketing: marketingRaw === 'true',
      personalization: personalizationRaw === 'true',
    };
  }

  // Fallback: try parsing fs-consent JSON (Finsuite format)
  const raw = getCookie('fs-consent');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(decodeURIComponent(raw)));
      if (parsed && (parsed.consents || parsed.choices)) {
        var choices = parsed.consents || parsed.choices;
        return {
          analytics: !!choices.analytics,
          marketing: !!choices.marketing,
          personalization: choices.personalization !== false,
        };
      }
    } catch (e) {
      // ignore parse error
    }
  }

  // No consent found — default all on (user hasn't interacted with banner)
  return {
    analytics: true,
    marketing: true,
    personalization: true,
  };
}

const Toggle = ({ checked, disabled, onChange }) => (
  <ToggleLabel>
    <ToggleInput type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
    <ToggleSlider checked={checked} disabled={disabled} />
  </ToggleLabel>
);

class MyPrivacy extends Component {
  constructor(props) {
    super(props);
    const state = readConsentState();
    this.state = {
      analytics: state.analytics,
      marketing: state.marketing,
      personalization: state.personalization,
      saving: false,
    };
  }

  handleSave = () => {
    const { analytics, marketing, personalization } = this.state;
    this.setState({ saving: true });

    // Use the global saveConsent logic from index.html
    // We replicate the cookie writes here since the banner functions
    // are scoped inside an IIFE
    const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const hostParts = window.location.hostname.split('.');
    const rootDomain = hostParts.length >= 2 ? '; domain=.' + hostParts.slice(-2).join('.') : '';
    const setCk = (name, val) => {
      document.cookie = name + '=' + val + '; path=/' + rootDomain + '; max-age=' + COOKIE_MAX_AGE + '; SameSite=Lax' + secure;
    };

    // Generate Finsuite-compatible ID
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
    let id = '';
    for (let i = 0; i < 21; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));

    const consentObj = {
      id,
      choices: {
        analytics,
        essential: true,
        marketing,
        personalization,
        uncategorized: false,
      },
    };

    if (!marketing) {
      if (getCookie('paloma_fbc') !== null) {
        document.cookie = 'paloma_fbc=; path=/; domain=.' + hostParts.slice(-2).join('.') + '; max-age=0';
      }

      if (getCookie('_fbp') !== null) {
        document.cookie = '_fbp=; path=/; domain=.' + hostParts.slice(-2).join('.') + '; max-age=0';
      }

      if (getCookie('_ga') !== null) {
        document.cookie = '_ga=; path=/; domain=.' + hostParts.slice(-2).join('.') + '; max-age=0';
      }

      if (getCookie('_gcl_aw') !== null) {
        document.cookie = '_gcl_aw=; path=/; domain=.' + hostParts.slice(-2).join('.') + '; max-age=0';
      }
    }

    setCk('fs-consent', encodeURIComponent(encodeURIComponent(JSON.stringify(consentObj))));
    setCk('fs-consent-security_storage', 'true');
    setCk('fs-consent-analytics_storage', String(analytics));
    setCk('fs-consent-ad_storage', String(marketing));
    setCk('fs-consent-ad_user_data', String(marketing));
    setCk('fs-consent-ad_personalization', String(marketing));
    setCk('fs-consent-personalization_storage', String(personalization));

    // Reload to apply changes (unloads/loads Segment destinations accordingly)
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  render() {
    const { analytics, marketing, personalization, saving } = this.state;

    return (
      <Wrapper>
        <Container>
          <Section>
            <Title>Privacy Preferences</Title>
            <Description>
              Manage how we use cookies and similar technologies. Essential cookies are always active as they are required for the site to function. You can adjust other categories below.
            </Description>

            <PrefRow>
              <PrefInfo>
                <PrefLabel>Essential</PrefLabel>
                <PrefDesc>Required for the site to function. Cannot be disabled.</PrefDesc>
              </PrefInfo>
              <Toggle checked disabled />
            </PrefRow>

            <PrefRow>
              <PrefInfo>
                <PrefLabel>Personalization</PrefLabel>
                <PrefDesc>Remembers your preferences and provides enhanced features.</PrefDesc>
              </PrefInfo>
              <Toggle checked={personalization} onChange={() => this.setState({ personalization: !personalization })} />
            </PrefRow>

            <PrefRow>
              <PrefInfo>
                <PrefLabel>Analytics</PrefLabel>
                <PrefDesc>Help us understand how visitors use our site.</PrefDesc>
              </PrefInfo>
              <Toggle checked={analytics} onChange={() => this.setState({ analytics: !analytics })} />
            </PrefRow>

            <PrefRow style={{ borderBottom: 'none' }}>
              <PrefInfo>
                <PrefLabel>Marketing</PrefLabel>
                <PrefDesc>Used to deliver relevant ads and track campaign performance.</PrefDesc>
              </PrefInfo>
              <Toggle checked={marketing} onChange={() => this.setState({ marketing: !marketing })} />
            </PrefRow>

            <Flex mt={3} alignItems="center">
              <Button variant="primary" onClick={this.handleSave} disabled={saving} style={{ minWidth: '180px' }}>
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </Flex>
          </Section>
        </Container>
      </Wrapper>
    );
  }
}

export default MyPrivacy;
